import type { PartialDeep } from 'type-fest';

import type { LoginState } from '@change-corgi/middle-end-gql-schema';

import type { LoginOrJoinMethod } from 'src/app/shared/utils/loginOrJoin';

import type { machine as controlActor } from './actors/control/controlActor';
import type { machine as recommendationsActor } from './actors/recommendations/recommendationsActor';

export const MediaTypes = {
	AI: 'AI_GENERATED',
	USER: 'USER_UPLOADED',
} as const;
type MetadataUser = {
	id?: string;
	loginOrJoinMethod?: LoginOrJoinMethod;
	initialLoginState?: Extract<LoginState, 'AUTHENTICATED' | 'GUEST'>;
};

export type Recommendation = {
	id: string;
	recommendation: string;
	why: string;
};

export type Draft = PartialDeep<{
	id: string;
	authorId: string;
	type:
		| 'ai_draft'
		| 'user_draft'
		| 'recommendations_variant_b_draft'
		| 'recommendations_variant_b_draft_ai'
		| 'recommendations_variant_b_draft_user'
		| null;
	input: {
		issuePrompt: string;
		personalStory: string;
		recommendations?: Recommendation[] | null;
		selectedRecommendation?: string;
	};
	recommendedDraft: {
		ask: string;
		description: string;
		image: { url: string; type: 'AI_GENERATED' } | null;
	} | null;
	petitionDraft: {
		ask: string | null;
		description: string | null;
		image: { url: string; type: 'USER_UPLOADED' | 'AI_GENERATED' } | null;
		reach: 'local' | 'national' | 'global';
		location: { id: string; name: string } | null;
	} | null;
	metadata: {
		draftMode: 'recommended_draft' | 'user_draft';
		imageJobId: string | null;
		imageFetchCount: number | null;
		imageGenerationCount: number;
		recommendationsJobId: string | null;
		user?: MetadataUser;
		variant?: 'control' | 'recommendations_variant_b';
	};
}>;

export type StateInput = object;

export type StateContext = { draft?: Draft | null };

export type StateEvents =
	| { type: 'back'; target?: null }
	| { type: 'submit'; draft?: Draft; target?: string | null }
	| { type: 'error' }
	| { type: 'sync_draft'; draft: Draft | null };

export type StateActions = {
	syncDraft: () => void;
	onSubmit: () => void;
	onError: () => void;
	// TODO: these actions should be removed after separating the control machine
	persistDraftType: () => void;
	persistRecommendationsDraftType: () => void;
	unsetDraftType: () => void;
	unsetRecommendationsDraftType: () => void;
};

export type StateGuards = {
	isControl: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isVariant: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	// TODO: these guards should be removed after separating the control machine
	transitionTo: (_: unknown, params: { target: string }) => boolean;
	isUserDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isAiDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isRecommendationsVariantBDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isRecommendationsVariantBAiDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isRecommendationsVariantBUserDraft: (
		args: { context: StateContext; event: StateEvents },
		params?: unknown,
	) => boolean;
};

export type StateChildren = {
	controlActorId: 'controlActorSrc';
	recommendationsActorId: 'recommendationsActorSrc';
};

export type StateActors = {
	controlActorSrc: typeof controlActor;
	recommendationsActorSrc: typeof recommendationsActor;
};
